import { faArrowRight, faTriangleExclamation, IconDefinition } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ButtonHTMLAttributes, CSSProperties, ReactNode, useEffect, useRef } from 'react'
import { twMerge } from 'tailwind-merge'

const skull = new URL('../../../assets/br1_skull.png', import.meta.url).href

/**
 * The house chrome: notched panels, slanted section tabs, and the states every section needs.
 *
 * This started life inside the Kill Race pages and is now shared, because the 2025 dashboard
 * mockups use the same motif and the account page needs it too. `features/career/components/
 * KillRaceUI` re-exports from here, so the Kill Race pages are unchanged.
 *
 * The motif is a *notched panel*: a rectangle with two opposite corners cut off. It is built with
 * `clip-path` on a 1px-padded wrapper — the wrapper paints the "border" and the inner surface
 * clips slightly tighter, so the bevel follows the notch instead of being squared off by a real
 * CSS border (which cannot follow a clip path).
 *
 * ⚠️ `clip-path` clips **every** descendant, including absolutely-positioned ones, and no
 * `z-index` escapes it. Anything that needs to overhang a panel — a dropdown, a popover, a
 * tooltip — must either render in flow inside the panel, or be portalled out of it. The account
 * page's wallet chooser learned this the expensive way: as an absolute menu it had its last row
 * sliced off at the panel edge, which silently hid wallets as the list grew.
 */
export const notch = (size: number) =>
    `polygon(0 0, calc(100% - ${size}px) 0, 100% ${size}px, 100% 100%, ${size}px 100%, 0 calc(100% - ${size}px))`

/** Slanted tab used for section headings — one cut, on the trailing edge. */
const tab = 'polygon(0 0, 100% 0, calc(100% - 16px) 100%, 0 100%)'

export const ACCENT = 'linear-gradient(94.38deg, #03CEFF -17.38%, #C91FFF 108.85%)'
export const PRIZE_GREEN = '#00FF90'
/** The panel interior. Darker than the page so a panel reads as inset, not stacked on top. */
export const SURFACE = '#08080b'

type PanelProps = {
    children: ReactNode
    className?: string
    /** Corner cut in px. Match it across siblings or the grid looks jittery. */
    size?: number
    /** Any CSS background — a flat rgba for resting state, `ACCENT` to highlight. */
    border?: string
    style?: CSSProperties
    id?: string
}

export const Panel = ({ children, className, size = 18, border = 'rgba(255,255,255,0.12)', style, id }: PanelProps) => (
    <div id={id} className='relative h-full p-px' style={{ background: border, clipPath: notch(size), ...style }}>
        <div className={twMerge('relative h-full bg-[#08080b]', className)} style={{ clipPath: notch(size - 1) }}>
            {children}
        </div>
    </div>
)

export const SectionHeading = ({
    icon,
    title,
    hint,
    right,
}: {
    icon?: IconDefinition
    title: string
    hint?: ReactNode
    right?: ReactNode
}) => (
    <div className='flex w-full flex-row items-center gap-2 md:gap-3'>
        {/*
          Heavier than the panels it heads. It used to be `bg-white/[0.07]` under a rule fading from
          `white/20`, while `Panel`'s own border is `white/12` — so the chrome that *separates*
          sections was dimmer than the chrome outlining each box inside them, and the page read as a
          uniform field of cards. The cyan edge is the section marker; the tab carries the weight.
        */}
        <div
            className='flex shrink-0 items-center gap-2.5 border-l-2 border-[#03CEFF] bg-white/[0.12] py-2 pl-3 pr-6 md:pl-4 md:pr-7'
            style={{ clipPath: tab }}
        >
            {icon && <FontAwesomeIcon icon={icon} className='text-xs text-[#03CEFF]' />}
            <h2 className='text-xs font-black uppercase tracking-[0.18em] md:text-base'>{title}</h2>
        </div>
        {hint && (
            <span className='hidden whitespace-nowrap text-2xs uppercase tracking-[0.2em] text-white/40 sm:inline'>
                {hint}
            </span>
        )}
        <div className='h-px flex-1 bg-gradient-to-r from-white/30 to-transparent' />
        {right}
    </div>
)

export const Chip = ({
    icon,
    children,
    className,
}: {
    icon?: IconDefinition
    children: ReactNode
    className?: string
}) => (
    <span
        className={twMerge(
            'inline-flex items-center gap-1.5 border border-white/15 bg-black/60 px-2.5 py-1',
            'text-2xs font-bold uppercase tracking-[0.15em] text-white/75 backdrop-blur-sm',
            className
        )}
    >
        {icon && <FontAwesomeIcon icon={icon} className='text-[0.85em] opacity-70' />}
        {children}
    </span>
)

/** Label-over-value pair. The label is chrome, so it is always uppercase. */
export const Stat = ({
    label,
    value,
    className,
    valueClassName,
    align = 'left',
}: {
    label: ReactNode
    value: ReactNode
    className?: string
    valueClassName?: string
    align?: 'left' | 'right'
}) => (
    <div className={twMerge('flex flex-col gap-1', align === 'right' && 'items-end text-right', className)}>
        <span className='text-2xs uppercase leading-none tracking-[0.2em] text-white/40'>{label}</span>
        <span className={twMerge('font-black leading-none tabular-nums', valueClassName)}>{value}</span>
    </div>
)

export const OutlineButton = ({
    children,
    className,
    ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { children: ReactNode }) => (
    <button
        className={twMerge(
            'inline-flex items-center justify-center gap-2 border border-white/25 px-5 py-2',
            // 44px floor: the mobile tap-target guideline the Inventory page was measured against.
            'min-h-[2.75rem] text-xs font-black uppercase tracking-[0.2em] transition-colors',
            'hover:border-white/60 hover:bg-white/5',
            className
        )}
        {...rest}
    >
        {children}
    </button>
)

/**
 * The page's answer to "there is nothing here" and "we could not load it".
 */
export const StateBlock = ({
    icon,
    image,
    title,
    children,
    action,
    className,
    size,
}: {
    icon?: IconDefinition
    image?: string
    title: string
    children?: ReactNode
    action?: ReactNode
    className?: string
    size?: number
}) => (
    <Panel size={size} className={twMerge('flex flex-col items-center justify-center gap-4 px-6 py-14', className)}>
        {image ? (
            <img src={image} className='h-14 w-14 opacity-25' alt='' />
        ) : (
            icon && <FontAwesomeIcon icon={icon} className='text-3xl text-white/25' />
        )}
        <h3 className='text-center text-base font-black uppercase tracking-[0.18em]'>{title}</h3>
        {children && (
            <p className='max-w-md text-center text-sm font-medium leading-relaxed text-white/45'>{children}</p>
        )}
        {action}
    </Panel>
)

export const EmptyBlock = (props: Omit<Parameters<typeof StateBlock>[0], 'icon'>) => (
    <StateBlock image={skull} {...props} />
)

export const ErrorBlock = ({
    title = "Couldn't load this",
    children = 'The server did not answer. This is usually temporary, so try again in a moment.',
    onRetry,
    size,
}: {
    title?: string
    children?: ReactNode
    onRetry?: () => void
    size?: number
}) => (
    <StateBlock
        icon={faTriangleExclamation}
        title={title}
        size={size}
        className='border-red-500/10'
        action={onRetry && <OutlineButton onClick={() => onRetry()}>Try again</OutlineButton>}
    >
        {children}
    </StateBlock>
)

export const Skeleton = ({ className }: { className?: string }) => (
    <div className={twMerge('animate-pulse bg-white/[0.06]', className)} />
)

/** Right-pointing affordance that slides on hover of an ancestor `.group`. */
export const GoArrow = ({ children, className }: { children?: ReactNode; className?: string }) => (
    <span
        className={twMerge(
            'flex items-center gap-2 text-xs font-black uppercase tracking-[0.2em]',
            'text-white/45 transition-colors group-hover:text-white',
            className
        )}
    >
        {children}
        <FontAwesomeIcon icon={faArrowRight} className='transition-transform group-hover:translate-x-1' />
    </span>
)

/**
 * Covers a feature that is built but not yet switched on.
 *
 * The children still render — a greyed-out shape of the real thing reads as "this will work here"
 * far better than an empty box does. They are made genuinely inert rather than merely
 * `pointer-events-none`: without `inert` the disabled controls underneath stay in the tab order,
 * so a keyboard user lands on a button that silently does nothing. React 18 has no `inert` prop,
 * hence the ref.
 */
export const ComingSoon = ({
    children,
    label = 'Coming soon',
    note,
    className,
}: {
    children: ReactNode
    label?: string
    note?: ReactNode
    className?: string
}) => {
    const ref = useRef<HTMLDivElement>(null)

    useEffect(() => {
        ref.current?.setAttribute('inert', '')
    }, [])

    return (
        <div className={twMerge('relative', className)}>
            <div ref={ref} className='select-none opacity-30 blur-[1.5px] grayscale'>
                {children}
            </div>
            <div className='absolute inset-0 flex flex-col items-center justify-center gap-1.5 bg-black/30 px-4 text-center backdrop-blur-[1px]'>
                <span
                    className='border border-[#03CEFF]/40 bg-black/70 px-3 py-1 text-2xs font-black uppercase tracking-[0.25em] text-[#03CEFF]'
                    style={{ clipPath: notch(6) }}
                >
                    {label}
                </span>
                {note && <span className='text-2xs font-bold uppercase tracking-wider text-white/50'>{note}</span>}
            </div>
        </div>
    )
}
